import {
  createAdminMessage,
  createMessage,
  createMessages,
  SB,
} from '@play-co/replicant';
import schema from '../../schema';
import { MAX_FRIEND_COUNT } from './ruleset/friends';
import {
  incrementScore,
  incrementTokenCreationCredits,
} from './game.modifiers';
import {
  initiateDailyTokenClaim,
  tmgInitState,
  updateOnchainHoldings,
  updatePointHolding,
} from '../tradingMeme/tradingMeme.modifiers';
import { DAY_IN_MS } from '../../utils/time';
import { getOwnMemeGiftId } from '../powerups/getters';
import { HP } from '../../lib/HighPrecision';

export const gameMessages = createMessages(schema)({
  addReferrerScore: createMessage(
    SB.object({ bonus: SB.int() }),
    (state, { bonus }, info) => {
      incrementScore(state, bonus);
    },
  ),
  addUnclaimedReferrerScore: createMessage(
    SB.object({ rewards: SB.int() }),
    (state, { rewards }, info) => {
      state.unclaimed_referral_rewards += rewards;
    },
  ),
  claimRewards: createMessage(
    SB.object({ rewards: SB.int() }),
    (state, { rewards }, info) => {
      incrementScore(state, rewards);
    },
  ),
  follow: createMessage(SB.object({}), (state, {}, info) => {
    state.followersCount++;
  }),
  unfollow: createMessage(SB.object({}), (state, {}, info) => {
    state.followersCount--;
  }),
  // Deprecates
  // @deprecated
  addFriendContribution: createMessage(SB.unknown(), (state, _, info) => {}),
  // @deprecated
  transferFriends: createMessage(SB.unknown(), (state, _, info) => {}),
  // @deprecated
  addFriend: createMessage(
    SB.object({
      friendId: SB.string(),
      bonus: SB.int(),
    }),
    (state, { friendId, bonus }, info) => {
      if (!state.friendIds) {
        // this message should not be called if friends are not defined
        console.error('addFriend called with undefined state.friendIds');
        return;
      }

      // if friend already exists, we dont want to add bonuses
      if (state.friendIds.includes(friendId)) {
        return;
      }

      // the player cannot have more than a given amount of friends
      // but can still receive bonuses from referrals
      if (state.friendIds.length < MAX_FRIEND_COUNT) {
        state.friendIds.push(friendId);
      }

      incrementScore(state, bonus);
    },
  ),
  // Admin messages
  addRewards: createAdminMessage(
    SB.object({ rewards: SB.int() }),
    (state, { rewards }, info) => {
      incrementScore(state, rewards);
    },
  ),
  addTokenCredits: createAdminMessage(
    SB.object({ credits: SB.int() }),
    (state, { credits }, info) => {
      incrementTokenCreationCredits(state, credits);
    },
  ),
  addGameTickets: createAdminMessage(
    SB.object({ tokenId: SB.string().optional(), tickets: SB.int() }),
    (state, { tokenId, tickets }, info) => {
      state.trading.miniGames.tapping.tickets += tickets;
    },
  ),
  creditReferrerKickBack: createMessage(
    SB.object({
      points: SB.number(),
      tokenId: SB.string(),
      pointUpdate: SB.object({
        timestamp: SB.int(),
        dailyPoints: SB.string().optional(),
        // @note: pointAmount not actually optional, should always exists
        pointAmount: SB.string().optional() as SB.StringSchema,
        currencyInvested: SB.string(),
        pointsAccumulated: SB.string(),
        lastNotifPrice: SB.string(),
        // @deprecated, replaced by pointAmount
        tokenAmount: SB.string().optional(),
      }),
    }),
    (state, props, info) => {
      const { tokenId, points, pointUpdate } = props;

      tmgInitState(state, tokenId);

      // Increase kickback counter;
      const allTimeReferralKickBack =
        state.trading.miniGames.state[tokenId].allTimeReferralKickBack;
      state.trading.miniGames.state[tokenId].allTimeReferralKickBack = HP(
        allTimeReferralKickBack,
      )
        .add(points)
        .toString();

      updatePointHolding(state, tokenId, {
        timestamp: pointUpdate.timestamp,
        dailyPoints: pointUpdate.dailyPoints,
        pointAmount:
          pointUpdate.pointAmount ||
          pointUpdate.tokenAmount ||
          HP(0).toString(),
        currencyInvested: pointUpdate.currencyInvested,
        lastNotifPrice: pointUpdate.lastNotifPrice,
        pointsAccumulated: pointUpdate.pointsAccumulated,
      });
    },
  ),
  setGiftAsClaimed: createMessage(
    SB.object({
      tokenId: SB.string(),
      shareTime: SB.int(),
    }),
    (state, props, info) => {
      const { tokenId, shareTime } = props;

      const giftId = getOwnMemeGiftId(tokenId, shareTime);
      const sentGift = state.trading.userMemeGiftsSent[giftId];
      if (!sentGift) {
        return;
      }

      if (sentGift.claimed) {
        return;
      }

      if (sentGift.shareTime + DAY_IN_MS < info.timestamp) {
        return;
      }

      sentGift.claimed = true;
    },
  ),
  initiateDailyTokenClaim: createMessage(
    SB.object({
      memeId: SB.string(),
    }),
    (state, payload, info) => {
      initiateDailyTokenClaim(state, payload.memeId, info.timestamp);
    },
  ),
  updateOnchainHoldings: createMessage(
    SB.object({
      memeId: SB.string(),
      walletAddress: SB.string(),
      jettonContractAddress: SB.string().optional(),
      jettonTokenAmount: SB.string().optional(),
      dexContractAddress: SB.string().optional(),
      dexTokenAmount: SB.string().optional(),
    }),
    (state, payload, info) => {
      updateOnchainHoldings(state, payload);
    },
  ),
});
